import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Filter, X, Calendar as CalendarIcon, RotateCcw,
  ChevronDown, ChevronUp, Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useUser } from '@/lib/UserContext';
import { HelpBubble } from '@/components/HelpBubble';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const ADDRESSABILITY_OPTIONS = ['Addressable', 'Non-Addressable', 'Unclassified'];

function AddressabilityFilter({ value, enabled, onChange }) {
  const select = (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? null : v)} disabled={!enabled}>
      <SelectTrigger data-testid="filter-addressability" className="h-9">
        <SelectValue placeholder="All Spend" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Spend</SelectItem>
        {ADDRESSABILITY_OPTIONS.map((o) => (
          <SelectItem key={o} value={o} data-testid={`filter-addressability-${o.toLowerCase()}`}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Addressability</label>
      {enabled ? select : (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div data-testid="filter-addressability-disabled" className="cursor-not-allowed">{select}</div>
            </TooltipTrigger>
            <TooltipContent>No addressability data uploaded</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * Standardized filter set order:
 * Category L1 → L2 → L3 → Supplier → Region → Country → SBU → Fiscal Year → Buyer → Date Range → Amount Range
 *
 * Each page declares which extras to show via `visibleFilters` prop.
 * Cascading: L1→L2→L3 and Region→Country are handled automatically.
 */

const FILTER_LABELS = {
  category: 'Category L1',
  category_l2: 'Category L2',
  category_l3: 'Category L3',
  supplier: 'Supplier',
  region: 'Region',
  country: 'Country',
  sbu: 'SBU',
  fiscal_year: 'Fiscal Year',
  buyer: 'Buyer',
  department: 'Office Location',
  cost_center: 'Cost Center',
  business_unit: 'Department',
  addressability: 'Addressability',
  startDate: 'Start Date',
  endDate: 'End Date',
  minAmount: 'Min Amount',
  maxAmount: 'Max Amount',
};

const DEFAULT_VISIBLE = [
  'category', 'category_l2', 'category_l3',
  'region', 'country', 'sbu', 'fiscal_year',
  'cost_center', 'business_unit',
];

export function FilterPanel({ 
  filters, 
  onFiltersChange, 
  filterOptions: externalFilterOptions,
  visibleFilters = DEFAULT_VISIBLE,
  compact = false,
  pageName = null,
  showDateRange = false,
  showAmountRange = false,
}) {
  // Collapsible behaviour:
  // - Filter panel ALWAYS starts collapsed; users explicitly expand it when
  //   they want to filter. Keeps the analytics surface as the primary focus.
  // - The expand/collapse chevron is always visible.
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [options, setOptions] = useState(externalFilterOptions || {});
  const [searchTerms, setSearchTerms] = useState({});
  const isSelfFetch = !externalFilterOptions;
  const fetchRef = useRef(null);
  const user = useUser();
  const orgScope = user?.organization_id || 'anon';
  const storageKey = pageName ? `pca_filters_${orgScope}_${pageName}` : null;

  // Fetch filter options with cascading params
  const fetchOptions = useCallback(async (currentFilters = {}) => {
    try {
      const params = new URLSearchParams();
      if (currentFilters.category) params.append('category', currentFilters.category);
      if (currentFilters.category_l2) params.append('category_l2', currentFilters.category_l2);
      if (currentFilters.region) params.append('region', currentFilters.region);
      const qs = params.toString();
      const response = await api.get(`/analytics/filter-options${qs ? `?${qs}` : ''}`);
      setOptions(response.data);
    } catch (e) {
      console.error('Failed to load filter options');
    }
  }, []);

  // On mount: fetch options if self-managing
  useEffect(() => {
    if (isSelfFetch) fetchOptions(filters);
  }, []);

  // Sync external filter options
  useEffect(() => {
    if (externalFilterOptions) setOptions(externalFilterOptions);
  }, [externalFilterOptions]);

  // Sync filters from parent
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };

    // Cascade resets
    if (key === 'category') {
      newFilters.category_l2 = null;
      newFilters.category_l3 = null;
    }
    if (key === 'category_l2') {
      newFilters.category_l3 = null;
    }
    if (key === 'region') {
      newFilters.country = null;
    }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(newFilters)); } catch {}
    }

    // Re-fetch options for cascading (L1→L2→L3, Region→Country)
    if (isSelfFetch && (key === 'category' || key === 'category_l2' || key === 'region')) {
      fetchOptions(newFilters);
    }
  };

  const clearFilter = (key) => {
    const newFilters = { ...localFilters, [key]: null };
    // Cascade clears
    if (key === 'category') { newFilters.category_l2 = null; newFilters.category_l3 = null; }
    if (key === 'category_l2') { newFilters.category_l3 = null; }
    if (key === 'region') { newFilters.country = null; }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(newFilters)); } catch {}
    }
    if (isSelfFetch && (key === 'category' || key === 'category_l2' || key === 'region')) {
      fetchOptions(newFilters);
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = Object.keys(localFilters).reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, {});
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(clearedFilters)); } catch {}
    }
    if (isSelfFetch) fetchOptions({});
  };

  // Only count filters that are visible on this page
  const effectiveVisibleKeys = new Set([
    ...visibleFilters,
    'addressability',
    ...(showDateRange || visibleFilters.includes('dateRange') ? ['startDate', 'endDate'] : []),
    ...(showAmountRange || visibleFilters.includes('amountRange') ? ['minAmount', 'maxAmount'] : []),
  ]);
  const activeFilterCount = Object.entries(localFilters).filter(([k, v]) => effectiveVisibleKeys.has(k) && v !== null && v !== '' && v !== 'all').length;

  // Map filter key → options array
  const optMap = {
    category: options.categories || [],
    category_l2: options.subcategories || [],
    category_l3: options.categories_l3 || [],
    supplier: options.suppliers || [],
    region: options.regions || [],
    country: options.countries || [],
    sbu: options.sbus || options.entities || [],
    fiscal_year: options.fiscal_years || options.fiscalYears || [],
    buyer: options.buyers || [],
    department: options.departments || options.office_locations || [],
    cost_center: options.cost_centers || [],
    business_unit: options.business_units || [],
  };

  const placeholders = {
    category: 'All Categories',
    category_l2: 'All Level 2',
    category_l3: 'All Level 3',
    supplier: 'All Suppliers',
    region: 'All Regions',
    country: 'All Countries',
    sbu: 'All SBUs',
    fiscal_year: 'All Years',
    buyer: 'All Buyers',
    department: 'All Locations',
    cost_center: 'All Cost Centers',
    business_unit: 'All Departments',
  };

  // Build ordered list of select filters to render
  const STANDARD_ORDER = [
    'category', 'category_l2', 'category_l3', 'supplier',
    'region', 'country', 'sbu', 'cost_center', 'business_unit',
    'fiscal_year', 'buyer', 'department',
  ];
  const selectFilters = STANDARD_ORDER.filter(k => visibleFilters.includes(k) && optMap[k]?.length > 0);

  return (
    <Card className="border-dashed" data-testid="filter-panel">
      <CardContent className="pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">Filters</span>
            <HelpBubble text="Global filters narrow every KPI, chart, and AI insight on this page. Filters persist as you navigate between pages. Clear All resets them for this page only." />
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7" data-testid="clear-all-filters">
                <RotateCcw className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            )}
            {compact && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-7"
                data-testid="filter-panel-toggle"
                aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
                title={isExpanded ? 'Collapse filters' : 'Expand filters'}
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
            {!compact && (
              // Non-compact pages still get a collapse control so users can
              // hide the filter grid on pages that pack a lot into screen real-estate.
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-7 text-xs"
                data-testid="filter-panel-toggle"
                aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
                title={isExpanded ? 'Collapse filters' : 'Expand filters'}
              >
                {isExpanded ? (
                  <><ChevronUp className="w-3.5 h-3.5 mr-1" />Collapse</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5 mr-1" />Expand</>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        {isExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {selectFilters.map((key) => {
              const items = optMap[key] || [];
              const searchable = items.length > 15;
              const searchTerm = (searchTerms[key] || '').toLowerCase();
              const filtered = searchable && searchTerm
                ? items.filter(item => item.toLowerCase().includes(searchTerm))
                : items;

              return (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">{FILTER_LABELS[key]}</label>
                  <Select
                    value={localFilters[key] || 'all'}
                    onValueChange={(v) => { handleFilterChange(key, v === 'all' ? null : v); setSearchTerms(p => ({ ...p, [key]: '' })); }}
                  >
                    <SelectTrigger data-testid={`filter-${key.replace(/_/g, '-')}`} className="h-9">
                      <SelectValue placeholder={placeholders[key]} />
                    </SelectTrigger>
                    <SelectContent>
                      {searchable && (
                        <div className="px-2 pb-1.5 pt-0.5 sticky top-0 bg-popover z-10">
                          <div className="flex items-center gap-1.5 px-2 border rounded-md">
                            <Search className="w-3 h-3 text-muted-foreground shrink-0" />
                            <input
                              type="text"
                              className="flex-1 h-7 text-xs bg-transparent outline-none placeholder:text-muted-foreground"
                              placeholder={`Search ${FILTER_LABELS[key]}...`}
                              value={searchTerms[key] || ''}
                              onChange={e => setSearchTerms(p => ({ ...p, [key]: e.target.value }))}
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => e.stopPropagation()}
                              data-testid={`filter-search-${key.replace(/_/g, '-')}`}
                            />
                          </div>
                        </div>
                      )}
                      <SelectItem value="all">{placeholders[key]}</SelectItem>
                      {filtered.map((item) => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                      {searchable && searchTerm && filtered.length === 0 && (
                        <div className="px-2 py-3 text-xs text-muted-foreground text-center">No matches</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}

            <AddressabilityFilter
              value={localFilters.addressability}
              enabled={!!options.has_addressability || !!localFilters.addressability}
              onChange={(v) => handleFilterChange('addressability', v)}
            />

            {/* Date Range */}
            {(showDateRange || visibleFilters.includes('dateRange')) && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Start Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full h-9 justify-start text-left font-normal", !localFilters.startDate && "text-muted-foreground")}
                        data-testid="filter-start-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {localFilters.startDate ? format(new Date(localFilters.startDate), 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={localFilters.startDate ? new Date(localFilters.startDate) : undefined}
                        onSelect={(date) => handleFilterChange('startDate', date?.toISOString())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">End Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full h-9 justify-start text-left font-normal", !localFilters.endDate && "text-muted-foreground")}
                        data-testid="filter-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {localFilters.endDate ? format(new Date(localFilters.endDate), 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={localFilters.endDate ? new Date(localFilters.endDate) : undefined}
                        onSelect={(date) => handleFilterChange('endDate', date?.toISOString())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}

            {/* Amount Range */}
            {(showAmountRange || visibleFilters.includes('amountRange')) && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Min Amount</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={localFilters.minAmount || ''}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value ? parseFloat(e.target.value) : null)}
                    className="h-9"
                    data-testid="filter-min-amount"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Max Amount</label>
                  <Input
                    type="number"
                    placeholder="No limit"
                    value={localFilters.maxAmount || ''}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value ? parseFloat(e.target.value) : null)}
                    className="h-9"
                    data-testid="filter-max-amount"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Active Filters Summary */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
            {Object.entries(localFilters).map(([key, value]) => {
              if (!value || value === 'all') return null;
              if (!effectiveVisibleKeys.has(key)) return null;
              const label = FILTER_LABELS[key] || key;
              return (
                <Badge key={key} variant="outline" className="flex items-center gap-1 text-xs">
                  <span>{label}:</span>
                  <span className="font-medium">
                    {value instanceof Date ? format(value, 'PP') : String(value)}
                  </span>
                  <X className="w-3 h-3 ml-1 cursor-pointer hover:text-destructive" onClick={() => clearFilter(key)} />
                </Badge>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FilterPanel;
